import { useState, useEffect } from "react";
import "./QuestionSettingsMultiple.css";
import { QuestionSelector } from "../question-selector/QuestionSelector";
import { Question, QuestionType, UpdateQuestionPayload } from "effno-core";
import { apiUpdateQuestion } from "../../services/QuestionService";
import toast from "react-hot-toast";
import { toastFail } from "../../config";
import { MySwitch } from "../form-widgets/MySwitch";

type QuestionSettingsMultipleProps = {
    question: Question;
    onChange: (question: Question) => void;
}

export const QuestionSettingsMultiple = ({question, onChange} : QuestionSettingsMultipleProps) => {

    const [settings, setSettings] = useState<any | null>(JSON.parse(question.settings));
    const [type, setType] = useState<QuestionType>(question.type);
    const [required, setRequired] = useState<boolean>(question.required);

    useEffect(() => {
        setSettings(JSON.parse(question.settings));
        setRequired(question.required);
        setType(question.type);
    }, [question]);

    const handleTypeChange = async (value: string) => {
        console.log("[QuestionSettingsMultiple/handleTypeChange] value: ", value);
    }

    const handleRequiredChange = async (checked: boolean) => {
        setRequired(checked);

        const payload: UpdateQuestionPayload = {
            questionId: question.id,
            type,
            title: question.title,
            description: question.description,
            required: checked,
            settings: JSON.stringify(settings),
            conditions: question.conditions
        }

        updateSettings(payload);
    }

    const handleSettingChange = async (e: any) => {
        console.log("[QuestionSettingsMultiple/handleSettingChange] e: ", e);
        console.log("[QuestionSettingsMultiple/handleSettingChange] checked: ", e.target.checked);


        let newSettings = {...settings};

        switch (e.target.name) {
            case "minChoices":
            case "maxChoices":
                newSettings = {...settings, [e.target.name]: parseInt(e.target.value)};
                break;
            case "otherValue":  
            default:
                newSettings = {...settings, [e.target.name]: e.target.value};
        }

        console.log("[QuestionSettingsMultiple/handleSettingChange] newSettings: ", newSettings);

        setSettings(newSettings);

        const payload: UpdateQuestionPayload = {
            questionId: question.id,
            type,
            title: question.title,
            description: question.description,
            required,
            settings: JSON.stringify(newSettings),
            conditions: question.conditions
        }

        updateSettings(payload)
    }

    const handleSwitchSettings = async (name: string, checked: boolean) => {
        console.log("[QuestionSettingsContact/handleSwitchSettings] name: ", checked);

        let newSettings = {...settings};

        switch (name) {
            case "multiSelectOn":
            case "randomizeOn":
            case "hasOtherOn":
            case "enforceMinOn":
            case "enforceMaxOn":
                newSettings = {...settings, [name]: checked};
                break;
            default:
                // nadad
        }

        setSettings(newSettings);

        const payload: UpdateQuestionPayload = {
            questionId: question.id,
            type,
            title: question.title,
            description: question.description,
            required,
            settings: JSON.stringify(newSettings),
            conditions: question.conditions
        }

        await updateSettings(payload)
    }

    // have to send in the payload here due to react setting asyncs
    const updateSettings = async(payload: UpdateQuestionPayload) => {

        const newQuestion = await apiUpdateQuestion(payload);

        if (!newQuestion) {
            toast("Error updating question", toastFail);
            return;
        }

        // update UI
        onChange(newQuestion);
    }

    console.log("[QuestionSettingsMultiple] settings: ", settings);

    return (        
        <>
            <div className="question-settings-multiple">
                {/* <QuestionSelector defaultValue={type} onChange={handleTypeChange}/>

                <div className="question-setting-divider">{}</div> */}

                <div className="question-setting">
                    <div className="question-setting-label">
                        Required
                    </div>
                    <div className="question-setting-checkbox">
                        <MySwitch 
                            id="required"
                            checked={required}
                            onChange={handleRequiredChange}
                        />
                    </div>
                </div>

                <div className="question-setting">
                    <div className="question-setting-label">
                        Multiple Selections
                    </div>
                    <div className="question-setting-checkbox">
                        <MySwitch 
                            id="multiSelectOn"
                            checked={settings?.multiSelectOn}
                            onChange={(checked: boolean) => 
                                handleSwitchSettings("multiSelectOn", checked)
                            }
                        />
                    </div>
                </div>

                <div className="question-setting">
                    <div className="question-setting-label">
                        Randomize
                    </div>
                    <div className="question-setting-checkbox">
                        <MySwitch 
                            id="randomizeOn"
                            checked={settings?.randomizeOn}
                            onChange={(checked: boolean) => 
                                handleSwitchSettings("randomizeOn", checked)
                            }
                        />
                    </div>
                </div>

                <div className="question-setting">
                    <div className="question-setting-label">
                        Has Other
                    </div>
                    <div className="question-setting-checkbox">
                        <MySwitch 
                            id="hasOtherOn"
                            checked={settings?.hasOtherOn}
                            onChange={(checked: boolean) => 
                                handleSwitchSettings("hasOtherOn", checked)
                            }
                        />
                    </div>
                </div>

                <div className="question-setting-stack">
                    <div className="question-setting-stack-top">
                        <div className="question-setting-label">
                            Min Choices
                        </div>
                        <div className="question-setting-radio">
                            <MySwitch 
                                id="enforceMinOn"
                                checked={settings?.enforceMinOn}
                                onChange={(checked: boolean) => 
                                    handleSwitchSettings("enforceMinOn", checked)
                                }
                            />
                        </div>
                    </div>
                        
                    {settings?.enforceMinOn &&
                        <div className="question-setting-stack-bottom">
                            <input 
                                type="number"
                                name="minChoices"
                                value={settings?.minChoices}
                                onChange={handleSettingChange}
                                data-testid="minChoices"
                            />
                        </div>
                    }

                </div>

                <div className="question-setting-stack">
                    <div className="question-setting-stack-top">
                        <div className="question-setting-label">
                            Max Choices
                        </div>
                        <div className="question-setting-radio">
                            <MySwitch 
                                id="enforceMaxOn"
                                checked={settings?.enforceMaxOn}
                                onChange={(checked: boolean) => 
                                    handleSwitchSettings("enforceMaxOn", checked)
                                }
                            />
                        </div>
                    </div>
                        
                    {settings?.enforceMaxOn &&
                        <div className="question-setting-stack-bottom">
                            <input 
                                type="number"
                                name="maxChoices"
                                value={settings?.maxChoices}
                                onChange={handleSettingChange}
                                data-testid="maxChoices"
                            />
                        </div>
                    }

                </div>
            </div>
            
        </>
    )
}